import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { JsonLd, useSEO } from '../utils/seo';

/**
 * Project guide index — /projects.
 *
 * The guides used to share one route, with the index and the detail rendered
 * together. Separating them gives each guide a page of its own and gives the
 * "which job am I doing?" entry point a URL that can rank on its own terms.
 */
export function Projects() {
  const projects = getProjects();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: 'Gravel & Landscaping Project Guides — Materials and Depths | 206Gravel',
    description: 'Pick your job — driveway, muddy driveway, French drain, patio base, retaining wall, path, or beds — and see the exact materials, depths, and yardage that job needs in the Puget Sound area.',
    path: '/projects'
  });
  // A project uses its own `image` when one has been set in data/projects.ts,
  // and otherwise borrows the photo of the material it leads with — so every
  // card has artwork today, and swapping in a real job-site photo later is a
  // single line in the project data with nothing here to change.
  const thumbnailFor = (project: {
    image?: string;
    materials: {
      slug: string;
    }[];
  }): string | undefined => {
    if (project.image) return project.image;
    for (const material of project.materials) {
      for (const cat of categories) {
        const product = cat.products.find(p => p.slug === material.slug);
        if (product) return product.images?.[0] || product.image;
      }
    }
    return undefined;
  };
  return <main className="min-h-screen bg-[#F2EFE8] dark:bg-[#262626] pt-8 pb-24" data-id="element-4000" data-mp="27hlpe0t9l50t">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Gravel and landscaping project guides',
      itemListElement: projects.map((p, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: p.name,
        url: `https://206gravel.com/projects/${p.slug}`
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Project Guides',
        item: 'https://206gravel.com/projects'
      }]
    }]} data-id="element-4001" data-mp="27jojavxjfiat" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4002" data-mp="22chsvi7g6cuu">
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-4003" data-mp="22awzvx0s71z3">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-4004" data-mp="1xg14cmwc5wh8">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-4005" data-mp="lyhwgh7t95cc" />
          <span className="text-stone-900 dark:text-white font-medium" data-id="element-4006" data-mp="lwyhctxw50c5">
            Project Guides
          </span>
        </nav>

        <div className="mb-6 h-1 w-16 rounded-full bg-brand-orange" data-id="element-4007" data-mp="2xk0mi8xiqr8x" />
        <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-baseline md:gap-6" data-id="element-4008" data-mp="32lhlu00cu5ne">
          <h1 className="font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-brand-charcoal dark:text-white md:text-5xl" data-id="element-4009" data-mp="37igc27gwdsgb">
            Project <span className="font-black text-[#04A05C]" data-id="element-4010" data-mp="3ha48kmsb9zon">Guides</span>
          </h1>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-4011" data-mp="3hc6uovqcf8o3">
            — Browse by project
          </span>
        </div>
        <p className="mb-10 max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-4012" data-mp="3qbk5brq7e3ae">
          Pick the job you're doing and we'll show the exact materials, depths,
          and quantities that job needs — then price it delivered.
        </p>

        {/* Photo-first tiles: the image is the whole card and the copy sits on
            an opaque bar across the bottom — black on solid white is the most
            legible option over mid-tone gravel photos, and it keeps the job
            itself as the thing the visitor reads. Someone who can't parse
            "5/8 minus" can still recognize a driveway. */}
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3" data-id="element-4013" data-mp="6odpf96lacc">
          {projects.map((p, index) => {
          const image = thumbnailFor(p);
          return <motion.li key={p.slug} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.22,
            delay: Math.min(index, 6) * 0.03
          }} data-id="element-4014" data-mp="38mgf5sv159w9" data-mpi={index}>
                <Link to={`/projects/${p.slug}`} className="group relative block aspect-[4/3] overflow-hidden rounded-xl bg-stone-300 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 dark:bg-white/10 dark:focus-visible:ring-offset-[#262626]" aria-label={`${p.name} project guide — ${p.tagline}`} title={p.tagline} data-id="element-4015" data-mp="3cmqu3v0svan9">
                  {image && <img src={image} alt="" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-4016" data-mp="2fkyx1mfds6s7" />}
                  <div className="absolute inset-x-0 bottom-0 bg-[#fffcf9] px-3 py-2.5 font-sub sm:px-4 dark:bg-[#1c1c1c]" data-id="element-4017" data-mp="25ri1acvoohlw">
                    <h2 className="truncate text-[15px] font-bold leading-tight text-stone-900 sm:text-lg dark:text-white" data-id="element-4018" data-mp="2yi01951422v3">
                      {p.name}
                    </h2>
                    <p className="mt-0.5 truncate text-[11px] leading-snug text-stone-600 sm:text-xs dark:text-white/70" data-id="element-4019" data-mp="2jndbzvfxime7">
                      {p.tagline}
                    </p>
                    <p className="mt-1 truncate text-[11px] font-semibold text-stone-500 dark:text-white/50" data-id="element-4020" data-mp="184iydkeeedfk">
                      {p.materials.length}{' '}
                      {p.materials.length === 1 ? 'material' : 'materials'} ·{' '}
                      {p.typicalYards}
                    </p>
                  </div>
                </Link>
              </motion.li>;
        })}
        </ul>

        <div className="mt-12 rounded-2xl border border-stone-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04] sm:p-8" data-id="element-4021" data-mp="35zy78zk32vtk">
          <h2 className="font-heading text-xl font-bold text-stone-900 dark:text-white" data-id="element-4022" data-mp="2w3ms81t3zrvf">
            Not sure which job you're doing?
          </h2>
          <p className="mt-2 max-w-2xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-4023" data-mp="2s18axm539069">
            Answer a few questions about your site and we'll name the material,
            the depth, and the amount.
          </p>
          <div className="mt-5 flex flex-wrap gap-3" data-id="element-4024" data-mp="2i5ojbbxk4nnb">
            <Link to="/help" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange/90 text-white font-bold text-sm transition-colors" data-id="element-4025" data-mp="lbtq2l0zr92y">
              Help me choose
              <ArrowRight className="w-4 h-4" data-id="element-4026" data-mp="nuyikn4ogb19" />
            </Link>
            <Link to="/calculator" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-stone-300 dark:border-white/15 text-stone-900 dark:text-white font-bold text-sm hover:bg-stone-100 dark:hover:bg-white/5 transition-colors" data-id="element-4027" data-mp="sut8ancpuukq">
              Calculate yardage
            </Link>
          </div>
        </div>
      </div>
    </main>;
}